'use client'

import { useEffect, useRef, useState } from 'react';
import { CountUp } from './components';

interface TProps {
  heading?: string
  subHeading?: string
  footerText?: string
  metrics?: {
    label: string;
    value: string;
    desc?: string;
  }[]
}

const MetricsSection = ({
  heading = "Numbers That Speak for Themselves",
  subHeading = "We empower events of all sizes with reliable tech and support.",
  footerText = "Built on real on-ground experience - crafted to make organizing effortless for everyone.",
  metrics,
}: TProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (sectionRef.current) observer.unobserve(sectionRef.current);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className='relative max-w-full bg-[#0B0D17] px-4 py-16 sm:px-6 xl:py-[100px] overflow-hidden'
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#1a1d2d] via-[#0B0D17] to-[#0B0D17] opacity-50 pointer-events-none" />

      <div className='relative z-10 flex flex-col text-center'>
        <h2 className='text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl leading-11 sm:leading-12 md:leading-16'>
          {heading}
        </h2>
        <p className='mt-2 text-base font-semibold text-gray-500'>
          {subHeading}
        </p>
      </div>

      <div className='relative z-10 mx-auto mt-12 grid max-w-[1320px] grid-cols-1 gap-6 px-4 text-center md:grid-cols-3 md:px-8 lg:gap-8'>
        {metrics?.map((metric) => (
          <div
            key={metric.label}
            className='group flex flex-col items-center justify-center gap-2 rounded-2xl border-b-[8px] border-primary cursor-pointer bg-white px-6 py-10 shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl lg:py-12'
          >
            <p className='text-4xl font-extrabold text-[#0B0D17] lg:text-5xl'>
              <CountUp target={metric.value} start={isVisible} />
            </p>
            <p className='text-base font-semibold text-gray-600 lg:text-lg'>
              {metric.label}
            </p>
            {metric?.desc && (
              <p className='text-sm font-semibold text-gray-500 lg:text-base'>
                {metric?.desc}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className='relative z-10 mt-16 text-center'>
        <div className='inline-flex items-center justify-center gap-2 rounded-full bg-white/5 px-6 py-3 text-sm font-medium text-gray-300 backdrop-blur-sm md:text-base'>
          <span>
            {footerText}
          </span>
        </div>
      </div>
    </section>
  );
}

export default MetricsSection;